/**
 * Frontend access entry point (US-044 AC1).
 *
 * Re-exports the SHARED permission predicate from `@livingsyncs/permissions` so
 * guards, navigation, and `useCanX` hooks all read permissions through one
 * implication-aware function — the same rules the backend enforces with. This
 * is advisory UX only; the backend remains the authoritative decision point
 * (AC4d).
 */
import { can, canAny, canAll } from "@livingsyncs/permissions";
import { keyFor, type PermissionKey } from "@livingsyncs/types";
import type { RouteAccess } from "../router/types";

export { can, canAny, canAll };

interface PermissionCarrier {
  permissions: string[];
}

/**
 * Static permission gate shared by `buildNav` (sidebar), `RouteGuard` (route
 * entry), and `useSurface` (tab/card surfaces) — US-009. Covers ONLY the static
 * rules so all three can never drift:
 *
 *   - `resource` → requires `${resource}:read` (implication-aware; US-050)
 *   - `anyOf`    → holds ANY of the keys
 *   - `allOf`    → holds ALL of the keys
 *
 * The runtime gate (`shiftRequired`) is deliberately NOT handled here: it is
 * evaluated at navigation time by `ShiftRequiredRoute` and must never hide a
 * link or a surface item (you navigate, then the shift modal runs).
 */
export function passesAccess(
  ctx: PermissionCarrier,
  access?: RouteAccess,
): boolean {
  if (!access) return true;
  if (access.resource) {
    const readKey = keyFor(access.resource, "read") as PermissionKey;
    if (!canAny(ctx, [readKey])) return false;
  }
  if (access.anyOf && !canAny(ctx, access.anyOf)) return false;
  if (access.allOf && !canAll(ctx, access.allOf)) return false;
  return true;
}
